import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Minus, Lock, Check, Clock, Sparkles, Gift } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { fmt, applyPromo } from "@/data/catalog";
import { PaymentIcons } from "@/components/common/PaymentIcons";
import { CandleUpsell } from "@/components/product/CandleUpsell";
import { getReservationSeconds, fmtClock } from "@/lib/timer";

function useCountdown() {
  const [secs, setSecs] = useState(getReservationSeconds);
  useEffect(() => {
    const t = setInterval(() => setSecs(getReservationSeconds()), 1000);
    return () => clearInterval(t);
  }, []);
  return fmtClock(secs);
}

export const CartDrawer = () => {
  const { open, setOpen, detailed, subtotal, updateQty, removeItem, count } = useCart();
  const timer = useCountdown();

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const compareTotal = detailed.reduce((s, i) => s + (i.compareAt || i.price) * i.quantity, 0);
  const savings = Math.max(0, compareTotal - subtotal);
  const { total: grandTotal } = applyPromo(subtotal);
  const displayItems = detailed.filter((i) => i.sku !== "protection");
  const displayCount = displayItems.reduce((s, i) => s + i.quantity, 0);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[80] bg-[#232222]/40 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 z-[90] flex h-full w-full max-w-md flex-col bg-white"
          >
            <div className="flex items-center justify-between px-6 py-5">
              <h3 className="font-display text-2xl text-[#2C2B2C]">Your Bag <span className="text-[#9A8E88]">({displayCount})</span></h3>
              <button onClick={() => setOpen(false)} data-testid="cart-close" className="rounded-full p-1.5 text-[#9A8E88] transition-colors hover:bg-[#FFFCFA] hover:text-[#2C2B2C]"><X className="h-5 w-5" /></button>
            </div>

            {detailed.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
                <p className="font-display text-2xl text-[#2C2B2C]">Your bag is empty</p>
                <p className="text-sm text-[#9A8E88]">Add the glow ritual everyone's talking about.</p>
                <Link to="/shop" onClick={() => setOpen(false)} data-testid="cart-empty-shop"
                  className="mt-2 rounded-full bg-[#232222] px-8 py-4 text-xs uppercase tracking-[0.18em] text-[#FFFCFA]">
                  Shop Now
                </Link>
              </div>
            ) : (
              <>
                {/* Almost-there progress + reserved countdown */}
                <div className="px-6 pb-4">
                  <div className="flex items-center justify-between text-xs">
                    <span className="inline-flex items-center gap-1.5 font-medium text-[#FBC4A8]"><Sparkles className="h-3.5 w-3.5" /> You're almost there</span>
                    <span data-testid="drawer-countdown" className="inline-flex items-center gap-1.5 text-[#9A8E88]"><Clock className="h-3.5 w-3.5 text-[#FBC4A8]" /> Reserved · <span className="font-semibold tabular-nums text-[#2C2B2C]">{timer}</span></span>
                  </div>
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-[#FFFCFA]">
                    <motion.div initial={{ width: 0 }} animate={{ width: "82%" }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                      className="h-full rounded-full bg-gradient-to-r from-[#FBC4A8] to-[#FBC4A8]" />
                  </div>

                  {/* Bonus shipping callout */}
                  <div className="mt-3 flex items-center gap-2.5 rounded-xl bg-[#FFFCFA] px-3.5 py-2.5" data-testid="drawer-bonus">
                    <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-[#FBC4A8]"><Gift className="h-3.5 w-3.5 text-white" /></span>
                    <p className="text-sm leading-tight text-[#2C2B2C]">
                      <span className="font-semibold">Bonus unlocked:</span> Free express shipping
                      {savings > 0 && <> · saving <span className="font-semibold text-[#FBC4A8]" data-testid="drawer-savings">{fmt(savings)}</span></>}
                    </p>
                  </div>
                </div>

                <div data-lenis-prevent className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6">
                  <div>
                    {displayItems.map((it, idx) => (
                      <div key={it.sku} data-testid={`cart-item-${it.sku}`} className={`flex gap-4 py-5 ${idx > 0 ? "border-t border-[#FFFCFA]" : ""}`}>
                        <div className="h-24 w-20 flex-none overflow-hidden rounded-xl bg-[#FFFCFA]">
                          <img src={it.image} alt={it.name} className="h-full w-full object-cover" />
                        </div>
                        <div className="flex flex-1 flex-col">
                          <div className="flex justify-between gap-2">
                            <p className="font-display text-lg leading-tight text-[#2C2B2C]">{it.name}</p>
                            <button onClick={() => removeItem(it.sku)} data-testid={`cart-remove-${it.sku}`} className="text-[#9A8E88] transition-colors hover:text-[#2C2B2C]"><X className="h-4 w-4" /></button>
                          </div>
                          <p className="mt-1 text-xs text-[#9A8E88]">{it.subtitle}</p>
                          <div className="mt-auto flex items-center justify-between pt-3">
                            <div className="flex items-center rounded-full border border-[#F2E8E6]">
                              <button onClick={() => updateQty(it.sku, it.quantity - 1)} data-testid={`cart-dec-${it.sku}`} className="px-2.5 py-1.5 text-[#9A8E88] hover:text-[#2C2B2C]"><Minus className="h-3 w-3" /></button>
                              <span className="w-8 text-center text-sm font-medium" data-testid={`cart-qty-${it.sku}`}>{it.quantity}</span>
                              <button onClick={() => updateQty(it.sku, it.quantity + 1)} data-testid={`cart-inc-${it.sku}`} className="px-2.5 py-1.5 text-[#9A8E88] hover:text-[#2C2B2C]"><Plus className="h-3 w-3" /></button>
                            </div>
                            <div className="text-right">
                              {it.compareAt > it.price && (
                                <span className="mr-1.5 text-xs text-[#9A8E88] line-through">{fmt(it.compareAt * it.quantity)}</span>
                              )}
                              <span className="font-display text-lg text-[#2C2B2C]">{fmt(it.price * it.quantity)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <CandleUpsell className="mt-3 mb-5" compact />
                </div>

                {/* Footer */}
                <div className="border-t border-[#FFFCFA] px-6 py-5">
                  {savings > 0 && (
                    <div className="mb-3 flex items-center justify-between rounded-xl border border-[#CDE9DA] bg-[#EDF7F1] px-3.5 py-2.5" data-testid="drawer-promo">
                      <span className="flex items-center gap-1.5 text-sm text-[#2C2B2C]">
                        <Check className="h-3.5 w-3.5 text-[#3B8C6E]" strokeWidth={3} /> Bundle savings
                      </span>
                      <span className="text-sm font-semibold text-[#3B8C6E]" data-testid="drawer-promo-amount">- {fmt(savings)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-sm uppercase tracking-[0.15em] text-[#9A8E88]">Total</span>
                    <div className="text-right">
                      {compareTotal > grandTotal && (
                        <span className="mr-2 align-middle text-sm text-[#9A8E88] line-through">{fmt(compareTotal)}</span>
                      )}
                      <span data-testid="cart-subtotal" className="font-display text-2xl text-[#2C2B2C]">{fmt(grandTotal)}</span>
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-[#9A8E88]">Free express shipping included · loved by 1,000+ women.</p>

                  <Link to="/cart" onClick={() => setOpen(false)} data-testid="cart-checkout"
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#232222] py-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#FFFCFA] transition-all hover:bg-[#3a3636]">
                    <Lock className="h-3.5 w-3.5" /> Secure Checkout
                  </Link>
                  <Link to="/cart" onClick={() => setOpen(false)} data-testid="cart-view"
                    className="mt-3 block w-full text-center text-xs uppercase tracking-[0.16em] text-[#9A8E88] transition-colors hover:text-[#2C2B2C]">
                    View full bag
                  </Link>

                  <PaymentIcons className="mt-5 justify-center" />
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
